/**
 * Authentication Types
 */

import { z } from 'zod';

/**
 * User schema returned by WorkOS-backed authentication endpoints.
 *
 * `userId` is the local `sys_user.user_id` UUID — never the WorkOS
 * `user_*` identifier, even when the user authenticated via WorkOS. The
 * API resolves the WorkOS `user_*` to the local row (via
 * `sys_user.auth_provider_id`) before returning the response. See
 * `docs/agents/identity-decisions.md` § "WorkOS user → local sys_user
 * mapping at login" for the rationale.
 *
 * This invariant lets the rest of the frontend treat user identity
 * uniformly across auth methods: `bill.createdBy`, audit logs, approval
 * history, and `req.user.userId` on the API are all the same UUID.
 */
export const UserSchema = z.object({
  userId: z.string().uuid(),
  email: z.string().email(),
  name: z.string(),
  tenantId: z.string().uuid(),
  active: z.boolean().optional(),
  /** User's primary role slug (first assigned role). */
  role: z.string().optional(),
  /** All assigned role slugs (WorkOS multi-role). */
  roles: z.array(z.string()).optional(),
  /** Permission keys from JWT claims. */
  permissions: z.array(z.string()).optional(),
  /** Per-authentication boundary used to partition authorization-sensitive browser caches. */
  sessionScopeId: z.string().uuid().optional(),
  /** Opaque server-derived identity of entity, payroll, FGA, role, and permission visibility. */
  authorizationScopeId: z.string().optional(),
});

export type User = z.infer<typeof UserSchema>;

/**
 * Tenant cache shape.
 *
 * Mirrors `sys_tenant` minimally — `tenantId` is required, `name` is
 * required so the UI can render it (sidebar, account menu). Both login
 * endpoints MUST return a complete `{ tenantId, name }` tenant object.
 */
export const TenantSchema = z.object({
  tenantId: z.string().uuid(),
  name: z.string().min(1),
  /** WorkOS organization id (`org_*`) when tenant is provisioned. */
  externalTenantId: z.string().nullable().optional(),
  slug: z.string().optional(),
  active: z.boolean().optional(),
});

export type Tenant = z.infer<typeof TenantSchema>;

/**
 * Login request schema
 */
export const LoginRequestSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
});

export type LoginRequest = z.infer<typeof LoginRequestSchema>;

/**
 * Login response schema
 */
export const LoginResponseSchema = z.object({
  csrfToken: z.string(),
  user: UserSchema,
  tenant: TenantSchema.optional(),
  authMethod: z.literal('workos').optional(),
});

export type LoginResponse = z.infer<typeof LoginResponseSchema>;

/**
 * Refresh token request schema
 */
export const RefreshTokenRequestSchema = z.object({});

export type RefreshTokenRequest = z.infer<typeof RefreshTokenRequestSchema>;

/**
 * Refresh token response schema
 */
export const RefreshTokenResponseSchema = z.object({
  user: UserSchema.optional(),
});

export type RefreshTokenResponse = z.infer<typeof RefreshTokenResponseSchema>;
